/**
 * UpdateScreen — pre-login update check.
 *
 * Shown at launch ONLY for a release that must not be skipped — a minor or
 * major bump, which by convention is where schema migrations and sync-contract
 * changes ride (see `updater.bump_kind`). App runs the check without blocking
 * render and mounts this screen only on that verdict, handing over the result
 * via `preloaded`.
 *
 * It deliberately no longer stands in front of every launch. It used to: the
 * app waited on a check that, in the overwhelmingly common case, had nothing to
 * report, and any available update was mandatory regardless of size. A patch
 * now downloads silently mid-session (Header.tsx) and installs itself at the
 * next start (`cli.py` → `apply_staged_update_if_any`), so users stop paying
 * attention to updates that carry nothing they need to know about.
 *
 * Still mandatory once shown: when the platform supports in-app auto-update
 * (`can_auto_update: true`) there is no Skip button. In dev mode and on
 * unsupported platforms the screen falls back to an external-download link plus
 * Skip, so developers don't get bricked when latest.json runs ahead of source.
 *
 * Uses raw fetch() instead of apiFetch() because there's no JWT yet.
 */

import { useEffect, useState, useRef } from "react";

type Phase = "checking" | "available" | "downloading" | "ready" | "applying" | "error" | "done";

interface UpdateInfo {
  update_available: boolean;
  current_version?: string;
  latest_version?: string;
  download_url?: string;
  sha256?: string;
  changelog?: string;
  can_auto_update?: boolean;
}

interface Props {
  /** Called when the user skips or no update is available. */
  onDone: () => void;
  /** Result of the check App already ran, so this screen doesn't repeat it. */
  preloaded?: UpdateInfo;
}

export function UpdateScreen({ onDone, preloaded }: Props) {
  const [phase, setPhase] = useState<Phase>(preloaded ? "available" : "checking");
  const [update, setUpdate] = useState<UpdateInfo | null>(preloaded ?? null);
  const [progress, setProgress] = useState({ downloaded: 0, total: 0 });
  const [errorMsg, setErrorMsg] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for updates on mount — skipped entirely when App already did it.
  // That is the normal path now: App renders this only for a blocking release
  // and hands over the result, so nobody waits on a second round-trip.
  useEffect(() => {
    if (preloaded) return;
    let timeout: ReturnType<typeof setTimeout>;

    const check = async () => {
      try {
        const res = await fetch("/api/updates/check");
        const data: UpdateInfo = await res.json();

        if (data.update_available && data.latest_version) {
          setUpdate(data);
          setPhase("available");
          return; // Stay on screen — user decides
        }
      } catch {
        // Check failed (no server, no internet, etc.) — continue to app
      }
      // No update or check failed — auto-proceed after brief flash
      setPhase("done");
      timeout = setTimeout(onDone, 300);
    };

    check();
    return () => {
      clearTimeout(timeout);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [onDone, preloaded]);

  const handleDownload = async () => {
    if (!update?.download_url) return;

    setPhase("downloading");

    // Start polling BEFORE the download request so we get progress updates
    // while the backend streams the file. The POST awaits the full download
    // so polling must begin first.
    pollRef.current = setInterval(async () => {
      try {
        const p = await fetch("/api/updates/progress");
        const data = await p.json();
        setProgress({ downloaded: data.progress, total: data.total });

        if (data.ready) {
          if (pollRef.current) clearInterval(pollRef.current);
          setPhase("ready");
        } else if (data.error) {
          if (pollRef.current) clearInterval(pollRef.current);
          setErrorMsg(data.error);
          setPhase("error");
        }
      } catch { /* ignore polling errors */ }
    }, 500);

    try {
      const res = await fetch("/api/updates/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          download_url: update.download_url,
          sha256: update.sha256 ?? "",
          version: update.latest_version ?? "",
        }),
      });

      const data = await res.json();
      if (!data.ok) {
        if (pollRef.current) clearInterval(pollRef.current);
        setErrorMsg(data.error || "Download failed");
        setPhase("error");
      }
    } catch (e) {
      if (pollRef.current) clearInterval(pollRef.current);
      setErrorMsg((e as Error).message);
      setPhase("error");
    }
  };

  const handleApply = async () => {
    setPhase("applying");
    try {
      const res = await fetch("/api/updates/apply", { method: "POST" });
      const data = await res.json().catch(() => null);

      // Only surface a failure if the backend explicitly reports one.
      // On Windows, apply schedules a detached installer + os._exit and
      // returns *before* the process actually exits — so a successful
      // response just means "scheduled". The connection dropping (caught
      // below) is the real signal of restart. Without this guard, Windows
      // users see a spurious "Restart failed" red banner just before the
      // app actually closes.
      if (data && data.ok === false) {
        setErrorMsg(data.error || "Restart failed — please relaunch the app manually.");
        setPhase("error");
      }
    } catch {
      // Connection dropped — process is restarting, expected.
    }
  };

  const pct = progress.total > 0 ? Math.round((progress.downloaded / progress.total) * 100) : 0;
  const downloadedMB = (progress.downloaded / 1048576).toFixed(1);
  const totalMB = (progress.total / 1048576).toFixed(1);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-jove-10 via-surface to-jove-30">
      <div className="w-full max-w-sm p-8 bg-surface rounded-2xl shadow-card text-center">
        {/* Logo */}
        <div className="mb-6">
          <img src="/cowork-logo.svg" alt="Nbyula OmniWerk" className="block w-12 h-12 rounded-xl mb-3 mx-auto" />
          <h1 className="text-lg font-bold text-space">Nbyula OmniWerk</h1>
        </div>

        {/* Checking */}
        {phase === "checking" && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4 text-jove" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <p className="text-sm text-space-50">Checking for updates...</p>
            </div>
          </div>
        )}

        {/* Update available */}
        {phase === "available" && update && (
          <div className="space-y-4">
            <div className="p-3 bg-jove-10 rounded-lg">
              <p className="text-sm font-medium text-space">
                v{update.latest_version} available
              </p>
              <p className="text-xs text-space-50 mt-1">
                You have v{update.current_version}
              </p>
            </div>

            {update.changelog && (
              <p className="text-xs text-space-40 text-left px-1">{update.changelog}</p>
            )}

            {update.can_auto_update ? (
              // Mandatory update path — no Skip. The user must apply
              // the update before reaching the app.
              <>
                <p className="text-xs text-space-50">
                  This update is required to continue.
                </p>
                <button
                  onClick={handleDownload}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-jove
                             rounded-lg hover:bg-jove-80 transition-colors"
                >
                  Update Now
                </button>
              </>
            ) : (
              // Dev mode / unsupported platform — keep Skip so we don't
              // brick devs when latest.json is ahead of the source tree.
              <div className="flex gap-2">
                <button
                  onClick={onDone}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-space-50 border border-space-30
                             rounded-lg hover:bg-space-10 transition-colors"
                >
                  Skip
                </button>
                <a
                  href={update.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setTimeout(onDone, 500)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-jove
                             rounded-lg hover:bg-jove-80 transition-colors inline-flex items-center justify-center"
                >
                  Download
                </a>
              </div>
            )}
          </div>
        )}

        {/* Downloading */}
        {phase === "downloading" && (
          <div className="space-y-3">
            <p className="text-sm text-space-50">Downloading update...</p>
            <div className="w-full bg-space-20 rounded-full h-2">
              <div
                className="bg-jove h-2 rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-space-40">
              {progress.total > 0 ? `${downloadedMB} / ${totalMB} MB (${pct}%)` : "Starting download..."}
            </p>
          </div>
        )}

        {/* Ready to apply */}
        {phase === "ready" && (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              <p className="text-sm font-medium text-space">Download complete</p>
            </div>
            <p className="text-xs text-space-50">The app will restart to apply the update.</p>
            <button
              onClick={handleApply}
              className="w-full px-4 py-2.5 text-sm font-medium text-white bg-jove
                         rounded-lg hover:bg-jove-80 transition-colors"
            >
              Restart & Update
            </button>
          </div>
        )}

        {/* Applying */}
        {phase === "applying" && (
          <div className="space-y-3">
            <svg className="animate-spin h-5 w-5 text-jove mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <p className="text-sm text-space-50">Restarting...</p>
          </div>
        )}

        {/* Error — error state is only reachable after attempting an
            auto-update, so the update is required by definition. No Skip;
            user can quit via the OS window controls if S3 is down. */}
        {phase === "error" && (
          <div className="space-y-3">
            <p className="text-sm text-hydra">{errorMsg}</p>
            <button
              onClick={handleDownload}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-jove
                         rounded-lg hover:bg-jove-80 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
